import moment from 'moment-timezone';

/**
 * Timezone label utilities.
 *
 * These helpers provide:
 * - a best-effort locale resolution (for formatting/translation lookups)
 * - a stable list of timezone options from `moment-timezone`
 * - optional i18n translation of specific timezone labels
 */

/**
 * Resolve a locale string in a defensive way (explicit param -> browser language -> fallback).
 * @param {string} locale
 * @returns {string}
 */
const resolveLocale = (locale) => {
  if (locale && typeof locale === 'string') {
    return locale;
  }

  if (typeof navigator !== 'undefined' && navigator.language) {
    return navigator.language;
  }

  return 'en';
};

/**
 * Get a label for a timezone.
 *
 * If a `translate` function is provided, we attempt to translate `timezones.<timezone>` first.
 *
 * @param {string} timezone
 * @param {string} locale
 * @param {(key: string, opts?: any) => string} translate
 * @returns {string}
 */
export const getTimezoneLabel = (timezone, locale, translate) => {
  if (!timezone) return '';

  if (typeof translate === 'function') {
    const translated = translate(`timezones.${timezone}`, { defaultValue: '' });
    if (translated) return translated;
  }

  return timezone;
};

/**
 * Alias kept for backward compatibility/readability in call sites.
 */
export const getTimezoneDisplayLabel = (timezone, locale, translate) =>
  getTimezoneLabel(timezone, locale, translate);

/**
 * Build a list of timezone options suitable for a `<select>`.
 *
 * @param {string} locale
 * @param {(key: string, opts?: any) => string} translate
 * @returns {{ value: string, label: string }[]}
 */
export const getTimezoneOptions = (locale, translate) => {
  const resolvedLocale = resolveLocale(locale);
  return moment.tz.names().map((tz) => ({
    value: tz,
    label: getTimezoneDisplayLabel(tz, resolvedLocale, translate),
  }));
};
